import React, { useCallback, useEffect, useRef, useState } from "react"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { formatJsonString, validateJsonObject } from "./utils"

interface JsonTextareaFieldProps {
  id: string
  label: string
  ariaLabel?: string
  placeholder: string
  description: string
  value: string | object | null | undefined
  fieldName: string
  onMetadataUpdate: (metadata: Record<string, unknown>) => void
  disabled?: boolean
  required?: boolean
  rows?: number
  validate?: (value: string) => string | null
  tooltip?: string
}

export const JsonTextareaField: React.FC<JsonTextareaFieldProps> = ({
  id,
  label,
  ariaLabel,
  placeholder,
  description,
  value,
  fieldName,
  onMetadataUpdate,
  disabled = false,
  required = false,
  rows = 4,
  validate = validateJsonObject,
  tooltip,
}) => {
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState(() => formatJsonString(value))
  const isEditingRef = useRef(false)

  useEffect(() => {
    if (!isEditingRef.current) {
      setText(formatJsonString(value))
    }
  }, [value])

  const handleValueChange = useCallback(
    (raw: string) => {
      setText(raw)

      if (raw.trim() === "") {
        setError(null)
      } else {
        setError(validate(raw))
      }

      onMetadataUpdate({ [fieldName]: raw })
    },
    [fieldName, onMetadataUpdate, validate],
  )

  const handleFocus = useCallback(() => {
    isEditingRef.current = true
  }, [])

  const handleBlur = useCallback(() => {
    isEditingRef.current = false
    const formatted = formatJsonString(text)
    setText(formatted)
    if (formatted !== text) {
      onMetadataUpdate({ [fieldName]: formatted })
    }
  }, [text, fieldName, onMetadataUpdate])

  return (
    <div className="space-y-2">
      <FieldLabel id={id} tooltip={tooltip}>
        {label} {required && <span className="text-coval-danger-text">*</span>}
      </FieldLabel>
      <JsonCodeEditor
        ariaLabel={ariaLabel || label}
        placeholder={placeholder}
        value={text}
        onChange={handleValueChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        rows={rows}
        disabled={disabled}
        error={error}
      />
      <p className="text-xs text-coval-text-muted">{description}</p>
    </div>
  )
}
